import React, { useState, useEffect } from 'react';
import { Flame } from 'lucide-react';
import { SEO } from '../components/SEO';

interface GasState {
  meterReading: string;
  previousReading: string;
  heatingArea: string;
  gasPrice: string;
  currency: 'EUR' | 'BAM' | 'RSD';
  period: 'monthly' | 'yearly';
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

// Conversion factors for different units
const CUBIC_METER_TO_KWH = 9.7; // 1 m³ ≈ 9.7 kWh
const AVERAGE_EFFICIENCY = 0.85; // 85% efficiency for modern gas boilers

function KalkulatorPotrošnjePlina() {
  const [values, setValues] = useState<GasState>({
    meterReading: '',
    previousReading: '',
    heatingArea: '',
    gasPrice: '',
    currency: 'EUR',
    period: 'monthly'
  });

  const [results, setResults] = useState<{
    consumption: number;
    energyKwh: number;
    cost: number;
    consumptionPerM2: number;
    efficiency: string;
  } | null>(null);

  const calculateConsumption = () => {
    const current = parseFloat(values.meterReading);
    const previous = parseFloat(values.previousReading);
    const area = parseFloat(values.heatingArea);
    const price = parseFloat(values.gasPrice);
    
    if (isNaN(current) || isNaN(previous) || isNaN(area) || isNaN(price)) {
      return null;
    }

    const consumption = current - previous;
    const energyKwh = consumption * CUBIC_METER_TO_KWH * AVERAGE_EFFICIENCY;
    const cost = consumption * price;
    const consumptionPerM2 = consumption / area;

    // Determine efficiency rating based on consumption per m²
    let efficiency = '';
    if (consumptionPerM2 < 0.8) efficiency = 'Izvrsna';
    else if (consumptionPerM2 < 1.2) efficiency = 'Dobra';
    else if (consumptionPerM2 < 1.6) efficiency = 'Prosječna';
    else efficiency = 'Potrebno poboljšanje';

    return {
      consumption: parseFloat(consumption.toFixed(2)),
      energyKwh: parseFloat(energyKwh.toFixed(2)),
      cost: parseFloat(cost.toFixed(2)),
      consumptionPerM2: parseFloat(consumptionPerM2.toFixed(2)),
      efficiency
    };
  };

  const handleInputChange = (field: keyof GasState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.meterReading && values.previousReading && values.heatingArea && values.gasPrice) {
      const results = calculateConsumption();
      setResults(results);
    }
  }, [values]);

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Potrošnje Plina",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrošnju plina, energetsku učinkovitost i mjesečne troškove. Precizni izračuni s detaljnim prikazom potrošnje po kvadratu.",
    "url": "https://online-kalkulator.com/kalkulator-potrosnje-plina",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun potrošnje plina",
      "Energetska učinkovitost",
      "Izračun troškova",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Potrošnje Plina | Izračun Potrošnje i Troškova Plina"
        description="Izračunajte potrošnju plina, energetsku učinkovitost i mjesečne troškove. Precizni izračuni s detaljnim prikazom potrošnje po kvadratu."
        canonicalUrl="/kalkulator-potrosnje-plina"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Flame className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Potrošnje Plina</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trenutno stanje brojila (m³)
                  </label>
                  <input
                    type="number"
                    value={values.meterReading}
                    onChange={(e) => handleInputChange('meterReading', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 1500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prethodno stanje brojila (m³)
                  </label>
                  <input
                    type="number"
                    value={values.previousReading}
                    onChange={(e) => handleInputChange('previousReading', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 1200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Površina grijanja (m²)
                  </label>
                  <input
                    type="number"
                    value={values.heatingArea}
                    onChange={(e) => handleInputChange('heatingArea', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 80"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cijena plina po m³
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.gasPrice}
                      onChange={(e) => handleInputChange('gasPrice', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite cijenu u ${values.currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value as GasState['currency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Period
                  </label>
                  <select
                    value={values.period}
                    onChange={(e) => handleInputChange('period', e.target.value as GasState['period'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Mjesečno</option>
                    <option value="yearly">Godišnje</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Potrošnja plina</div>
                      <div className="text-xl font-bold text-blue-600">{formatNumber(results.consumption)} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Energija</div>
                      <div className="text-xl font-bold text-blue-600">{formatNumber(results.energyKwh)} kWh</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Trošak:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatNumber(results.cost)} {currencySymbols[values.currency]}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Potrošnja po m²:</span>
                    <span className="font-semibold">
                      {formatNumber(results.consumptionPerM2)} m³/m²
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Energetska učinkovitost:</span>
                    <span className="font-semibold">
                      {results.efficiency}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Potrošnje Plina</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator potrošnje plina je koristan alat koji vam pomaže izračunati potrošnju plina,
                  energetsku učinkovitost i troškove grijanja. Uzima u obzir različite faktore kao što su
                  stanje brojila, površina grijanja i cijena plina kako bi vam pružio detaljan uvid u
                  vašu potrošnju.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Izračunati Potrošnju</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Očitajte trenutno stanje plinskog brojila</li>
                  <li>Unesite prethodno stanje brojila</li>
                  <li>Unesite površinu prostora koji se grije</li>
                  <li>Unesite trenutnu cijenu plina po m³</li>
                  <li>Odaberite valutu i period izračuna</li>
                  <li>Kalkulator će automatski izračunati:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Ukupnu potrošnju u m³</li>
                      <li>- Energetsku vrijednost u kWh</li>
                      <li>- Ukupni trošak</li>
                      <li>- Potrošnju po m²</li>
                      <li>- Ocjenu energetske učinkovitosti</li>
                    </ul>
                  </li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Energetska Učinkovitost</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">Izvrsna:</span>
                    <span>&lt; 0.8 m³/m²</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="font-medium">Dobra:</span>
                    <span>0.8 - 1.2 m³/m²</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="font-medium">Prosječna:</span>
                    <span>1.2 - 1.6 m³/m²</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">Potrebno poboljšanje:</span>
                    <span>&gt; 1.6 m³/m²</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Uštedu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Redovito održavajte plinski sustav</li>
                  <li>Izolirajte zidove i krov</li>
                  <li>Zamijenite staru stolariju</li>
                  <li>Optimizirajte temperaturu grijanja</li>
                  <li>Koristite programabilni termostat</li>
                  <li>Redovito provjetravajte prostorije</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Stvarna potrošnja može varirati ovisno o:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Vanjskoj temperaturi</li>
                      <li>- Izolaciji objekta</li>
                      <li>- Načinu korištenja grijanja</li>
                      <li>- Učinkovitosti plinskog kotla</li>
                    </ul>
                  </li>
                  <li>Redovito pratite potrošnju za bolju kontrolu troškova</li>
                  <li>Konzultirajte stručnjaka za detaljnu analizu</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Konverzije i Formule</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>1 m³ prirodnog plina ≈ 9.7 kWh energije</li>
                  <li>Prosječna učinkovitost modernih plinskih kotlova: 85-95%</li>
                  <li>Potrošnja po m² = Ukupna potrošnja / Površina grijanja</li>
                  <li>Ukupni trošak = Potrošnja × Cijena po m³</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorPotrošnjePlina;